import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../../../config';
import { toastError, errorMessage } from '../../../lib/toast';
import { formatDate } from '../../../lib/formatDate';

export default function AppRequestsQueue({ onCountChange, refreshKey }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const mountedRef = useRef(true);
  // Set true on (re)mount — see ActiveAppsList note: a declaration-only ref
  // stays false after StrictMode's first cleanup and strands setLoading(false).
  useEffect(() => { mountedRef.current = true; return () => { mountedRef.current = false; }; }, []);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/admin/app_requests`, {
        params: { status: 'pending', size: 100 }, withCredentials: true,
      });
      if (mountedRef.current) {
        setRows(res.data.rows);
        if (onCountChange) onCountChange(res.data.total);
      }
    } catch (err) {
      toastError(errorMessage(err, 'Couldn’t load app requests. Try again.'));
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [onCountChange]);

  // refreshKey is an optional counter bumped by the parent to force a reload
  // (e.g. after an admin creates a request on behalf of a user).
  useEffect(() => { load(); }, [load, refreshKey]); // eslint-disable-line

  return (
    <div>
      <div className="admin-table-count" style={{ marginBottom: 8 }}>
        {loading ? 'Loading…' : `${rows.length} pending app request${rows.length === 1 ? '' : 's'}`}
      </div>

      {!loading && rows.length === 0 ? (
        <div className="admin-table-wrap" style={{ textAlign: 'center', padding: 48, color: '#718096' }}>
          No pending app requests. The queue is clear.
        </div>
      ) : !loading && (
        <div className="admin-table-wrap ut-tablewrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>App</th>
                <th>Requester</th>
                <th style={{ width: 90 }}>Tools</th>
                <th style={{ width: 150 }}>Submitted</th>
                <th style={{ width: 120, textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id}>
                  <td><div className="at-primary">{r.name}</div></td>
                  <td>
                    <div className="at-secondary">{r.requester_name}</div>
                  </td>
                  <td className="at-muted">{r.tool_count}</td>
                  <td className="at-muted">{formatDate(r.created_at)}</td>
                  <td style={{ textAlign: 'right' }}>
                    <button type="button" className="btn btn-small btn-secondary" onClick={() => navigate(`/admin/apps/${r.id}`)}>
                      Review
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
